/** Kodiak (/client) navigation — curated by nav item status (#384).
 *
 * The client portal shipped every page it had on the menu, including three
 * surfaces it has not finished: the embedded-dashboard viewer, the threshold
 * editor and the organization tree. A client_admin who opened them found
 * either an empty page or an operator-facing screen.
 *
 * They are marked `legacy` and reuse the mechanism from #382: the route stays
 * mounted so deep links and bookmarks keep working, the entry leaves the menu
 * unless the build sets `VITE_SHOW_LEGACY_NAV=true`, and whoever still reaches
 * the page is told why it looks unfinished.
 *
 * Devices are deliberately absent: they belong to a project, so they are
 * reached from the project home and the site home (#380), never from a
 * top-level entry that could not name a project.
 */

import {
  Home,
  FileText,
  FolderKanban,
  MapPin,
  BarChart3,
  Settings2,
  ClipboardList,
  Network,
  ShieldCheck,
} from "lucide-react";
import type { TranslationKey } from "@/lib/i18n";
import type { NavItem, NavItemStatus } from "./adminNav";

// ---------------------------------------------------------------------------
// Canonical Kodiak status table
//
// One source of truth for what each client surface is. The static nav below
// declares the same status inline (a contract test keeps the two in step) and
// the heritage notice reads the same table, so a page cannot leave the menu
// without telling a client who deep-links into it why.
// ---------------------------------------------------------------------------

/** Not part of Core yet — mounted for deep links, off the menu. */
const CLIENT_LEGACY_PREFIXES = [
  "/client/analytics",
  "/client/thresholds",
  "/client/hierarchy",
];

function matchesRoutePrefix(href: string, prefix: string): boolean {
  return href === prefix || href.startsWith(`${prefix}/`);
}

/**
 * Canonical status for a Kodiak href, or `undefined` when the surface is a
 * finished one (i.e. live). Prefix-scoped so sub-routes inherit the verdict of
 * their section, and boundary-aware so a future `/client/analytics-export`
 * is not mistaken for `/client/analytics`.
 */
export function clientNavStatus(href: string): NavItemStatus | undefined {
  if (
    CLIENT_LEGACY_PREFIXES.some((prefix) => matchesRoutePrefix(href, prefix))
  ) {
    return "legacy";
  }
  return undefined;
}

/** What the heritage notice on a hidden Kodiak page says. */
export interface ClientLegacyNotice {
  /** Per-session dismissal key for the banner. */
  moduleName: string;
  /** Why this page is still here and where the answer lives instead. */
  hintKey: TranslationKey;
}

const CLIENT_LEGACY_NOTICES: Array<ClientLegacyNotice & { prefix: string }> = [
  {
    prefix: "/client/analytics",
    moduleName: "client-dashboards",
    hintKey: "client.legacyNotice.hint.dashboards",
  },
  {
    prefix: "/client/thresholds",
    moduleName: "client-thresholds",
    hintKey: "client.legacyNotice.hint.thresholds",
  },
  {
    prefix: "/client/hierarchy",
    moduleName: "client-hierarchy",
    hintKey: "client.legacyNotice.hint.hierarchy",
  },
];

/**
 * The heritage notice a Kodiak path must show, or `null` for a finished page.
 *
 * Driven by the path rather than wired route by route, for the same reason the
 * Command shell does it (#382): the hidden surfaces are exactly the ones the
 * menu drops, and one lookup keeps the two from drifting.
 */
export function clientLegacyNotice(
  pathname: string,
): ClientLegacyNotice | null {
  if (clientNavStatus(pathname) !== "legacy") return null;
  const match = CLIENT_LEGACY_NOTICES.find((notice) =>
    matchesRoutePrefix(pathname, notice.prefix),
  );
  if (!match) return null;
  return { moduleName: match.moduleName, hintKey: match.hintKey };
}

// ---------------------------------------------------------------------------
// Kodiak menu
//
// Live: the portfolio home, the projects a client owns, its sites and the
// activity trail. Everything else waits below, marked `legacy`.
// ---------------------------------------------------------------------------
export const clientMainNav: NavItem[] = [
  {
    name: "Home",
    labelKey: "nav.home",
    href: "/client/dashboard",
    icon: Home,
  },
  {
    name: "Projects",
    labelKey: "nav.projects",
    href: "/client/projects",
    icon: FolderKanban,
  },
  // D-AE5 = B: before gate 5 nothing is published, but the proposals sent at
  // gate 3 already belong to the client (frozen PDFs, read-only).
  {
    name: "Proposals",
    labelKey: "nav.proposals",
    href: "/client/proposals",
    icon: FileText,
  },
  {
    name: "Vigilancia",
    labelKey: "nav.vigilancia",
    href: "/client/vigilancia",
    icon: ShieldCheck,
    matchPrefix: "/client/vigilancia",
  },
  { name: "Sites", labelKey: "nav.sites", href: "/client/sites", icon: MapPin },

  // === NOT PART OF CORE YET — hidden unless VITE_SHOW_LEGACY_NAV ===
  {
    name: "Dashboards",
    labelKey: "nav.dashboards",
    href: "/client/analytics",
    icon: BarChart3,
    status: "legacy",
  },
  {
    name: "Organization",
    labelKey: "nav.organization",
    href: "/client/hierarchy",
    icon: Network,
    status: "legacy",
  },
];

export const clientBottomNav: NavItem[] = [
  {
    name: "Audit Log",
    labelKey: "nav.auditLog",
    href: "/client/audit",
    icon: ClipboardList,
  },
  {
    name: "Thresholds",
    labelKey: "nav.thresholds",
    href: "/client/thresholds",
    icon: Settings2,
    status: "legacy",
  },
];
